import { Card, Typography } from "../material-old";
import { Button, message, Space, Tag } from "antd";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks.ts";
import { fetchUserByID } from "../../redux/actions/usersActions.tsx";
import { CoreLoading } from "../common/CoreLoading.tsx";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  FolderOpenFilled,
  MailFilled,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import { BreadcrumbComponent } from "./BreadcrumbComponent.tsx";
import { userGroupColors } from "./fixtures/userGroupColors.ts";
import { extractErrorMessage } from "../../utils/extractErrorMessage.ts";
import { useLocation, useNavigate } from "react-router-dom";
import {
  darkBlue,
  gray,
  green,
  lightBlue,
  orange,
  red,
} from "../../utils/theme/colors.ts";

interface Props {
  id?: string;
  isProfile?: boolean;
}

export const Detail: React.FC<Props> = ({ id, isProfile }) => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const location = useLocation();

  const [messageApi, contextHolder] = message.useMessage();
  const { activeUser, loading } = useAppSelector((state) => state.user);
  const { userInfo } = useAppSelector((state) => state.auth);
  const user = pathname.includes("users/") ? activeUser : userInfo;

  useEffect(() => {
    handleInitializeUser();
  }, [pathname]);

  const handleInitializeUser = async () => {
    try {
      if (pathname.includes("users/") && id)
        await dispatch(fetchUserByID(id)).unwrap();
    } catch (error: any) {
      messageApi.error(extractErrorMessage(error, "No se pudo cargar el usuario."));
    }
  };

  const onEdit = () => {
    if (location.pathname.includes("users")) {
      navigate(`/users/${activeUser?.id}/edit`);
    } else {
      navigate(`/auth/profile/edit`);
    }
  };

  if (loading || !user) return <CoreLoading size={1} />;

  const userHead = `${user.name} - ${user.email}`;
  const verifiedIcon = user.is_validated ? (
    <CheckCircleFilled className="h-5 w-5" style={{ color: green }} />
  ) : (
    <CloseCircleFilled className="h-5 w-5" style={{ color: red }} />
  );
  const verifiedText = user.is_validated ? "verificado" : "no verificado";

  return (
    <Card className={`mx-auto py-8 px-20 md:w-8/12 mt-12`}>
      {contextHolder}
      {
        <div className="flex justify-end">
          <BreadcrumbComponent
            pathText={isProfile ? "Mi perfil" : userHead}
            isProfile={isProfile}
          />
        </div>
      }
      <div className="text-center mt-4">
        <Space>
          <Typography variant="h5" color="blue-gray">
            {user.name}
          </Typography>
        </Space>
      </div>
      <hr className="mt-4" />

      <div className="text-sm mt-8 flex flex-col w-full">
        <Space className="mt-2">
          <MailFilled className="w-5 h-5" style={{ color: orange }} />
          <span>
            <b>Correo:</b> {user.email}
          </span>
        </Space>
        <Space className="mt-2">
          <UsergroupAddOutlined className="w-5 h-5" style={{ color: "#000" }} />
          <span>
            <b>Grupos:&nbsp;</b>
            {user.groups.map((group, index) => (
              <Tag key={index} color={userGroupColors[group!]}>
                {group}
              </Tag>
            ))}
          </span>
        </Space>
        <Space className="mt-2">
          {verifiedIcon}
          <span>
            <b>Usuario:</b> {verifiedText}
          </span>
        </Space>
        <Space className="mt-2">
          <FolderOpenFilled className="w-5 h-5" style={{ color: darkBlue }} />
          <span>
            <b>Expedientes:</b> {user.folder_count}
          </span>
        </Space>
        <Space className="mt-2">
          <FolderOpenFilled className="w-5 h-5" style={{ color: lightBlue }} />
          <span>
            <b>Expedientes activos:</b> {user.active_folder_count}
          </span>
        </Space>
        <Space className="mt-2">
          <FolderOpenFilled className="w-5 h-5" style={{ color: gray }} />
          <span>
            <b>Expedientes inactivos:</b> {user.inactive_folder_count}
          </span>
        </Space>
      </div>

      <div className="w-full flex justify-end">
        <Button type="default" onClick={onEdit}>
          Editar
        </Button>
      </div>
    </Card>
  );
};
